import { inject, Injectable, signal } from '@angular/core';
import {
  collection,
  doc,
  Firestore,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from '@angular/fire/firestore';
import { createUserWithEmailAndPassword, getAuth, signOut } from 'firebase/auth';
import { getApps, initializeApp } from 'firebase/app';
import { environment } from '../../../environments/environment';
import { Tenant } from '../models';

export interface CreateTenantData {
  name: string;
  domain: string;
  timezone: string;
  locale: string;
  plan: Tenant['subscription']['plan'];
  seats: number;
  adminEmail: string;
  adminDisplayName: string;
}

export interface TenantAdminProvisioningResult {
  tenantId: string;
  adminUserId: string;
  adminTempPassword: string;
}

export interface UpdateTenantData {
  name: string;
  domain: string;
  timezone: string;
  locale: string;
  plan: Tenant['subscription']['plan'];
  seats: number;
}

@Injectable({ providedIn: 'root' })
export class PlatformTenantService {
  private readonly firestore = inject(Firestore);

  private readonly _tenants = signal<Tenant[]>([]);
  private readonly _loading = signal(false);

  readonly tenants = this._tenants.asReadonly();
  readonly loading = this._loading.asReadonly();

  async loadTenants(): Promise<void> {
    this._loading.set(true);
    try {
      const tenantsRef = collection(this.firestore, 'tenants');
      const q = query(tenantsRef, orderBy('name'));
      const snap = await getDocs(q);

      this._tenants.set(
        snap.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            name: data['name'],
            domain: data['domain'],
            settings: data['settings'],
            subscription: data['subscription'],
            createdAt: (data['createdAt'] as Timestamp)?.toDate() ?? new Date(),
            updatedAt: (data['updatedAt'] as Timestamp)?.toDate() ?? new Date(),
          } as Tenant;
        })
      );
    } finally {
      this._loading.set(false);
    }
  }

  async createTenant(data: CreateTenantData): Promise<TenantAdminProvisioningResult> {
    const tenantId = crypto.randomUUID();
    const tenantRef = doc(this.firestore, `tenants/${tenantId}`);
    const adminPassword = this.generateTempPassword();

    const adminAuthUser = await this.createAdminAuthUser(data.adminEmail, adminPassword);

    await setDoc(tenantRef, {
      name: data.name,
      domain: data.domain.toLowerCase(),
      settings: {
        timezone: data.timezone,
        locale: data.locale,
        fiscalYearStart: 1,
        dateFormat: 'dd/MM/yyyy',
        weekStartsOn: 1,
        auditLoggingLevel: 'standard',
        defaultLanguage: 'en',
        baseCurrency: 'USD',
        compensationSalaryTypes: ['monthly', 'hourly'],
        requireTwoFactorAuth: false,
        documents: {
          defaultRetentionYears: 6,
          permissions: {
            managerCanViewDirectReports: true,
            sensitiveVisibleToManagers: false,
          },
          categories: [
            { id: 'contracts', label: 'Contracts', restricted: false, sensitive: false },
            { id: 'policies', label: 'Policies', restricted: false, sensitive: false },
            { id: 'disciplinary', label: 'Disciplinary', restricted: true, sensitive: true },
            { id: 'payroll', label: 'Payroll', restricted: true, sensitive: true },
            { id: 'compliance', label: 'Compliance', restricted: false, sensitive: false },
            { id: 'medical', label: 'Medical', restricted: true, sensitive: true },
          ],
          types: [
            {
              id: 'employment_contract',
              label: 'Employment contract',
              categoryId: 'contracts',
              defaultRetentionYears: 10,
              requiresAcknowledgement: true,
              requiresSignature: true,
              allowedScopes: ['employee'],
              restricted: false,
              sensitive: false,
            },
            {
              id: 'warning_letter',
              label: 'Warning letter',
              categoryId: 'disciplinary',
              defaultRetentionYears: 6,
              requiresAcknowledgement: true,
              requiresSignature: false,
              allowedScopes: ['employee'],
              restricted: true,
              sensitive: true,
            },
            {
              id: 'leave_approval',
              label: 'Leave approval',
              categoryId: 'compliance',
              defaultRetentionYears: 3,
              requiresAcknowledgement: false,
              requiresSignature: false,
              allowedScopes: ['employee'],
              restricted: false,
              sensitive: false,
            },
            {
              id: 'nda',
              label: 'NDA',
              categoryId: 'contracts',
              defaultRetentionYears: 6,
              requiresAcknowledgement: true,
              requiresSignature: true,
              allowedScopes: ['employee'],
              restricted: false,
              sensitive: false,
            },
            {
              id: 'termination_letter',
              label: 'Termination letter',
              categoryId: 'contracts',
              defaultRetentionYears: 10,
              requiresAcknowledgement: true,
              requiresSignature: true,
              allowedScopes: ['employee'],
              restricted: true,
              sensitive: true,
            },
          ],
        },
        organization: {
          name: data.name,
          addressLine1: '',
          addressLine2: null,
          city: '',
          state: '',
          postalCode: '',
          country: 'US',
        },
        documentUpload: {
          maxSizeMb: 10,
          allowedTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg'],
        },
        expenseReceiptUpload: {
          maxSizeMb: 10,
          allowedTypes: ['.pdf', '.png', '.jpg', '.jpeg'],
        },
      },
      subscription: {
        plan: data.plan,
        seats: data.seats,
        expiresAt: null,
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    await setDoc(doc(this.firestore, `tenants/${tenantId}/users/${adminAuthUser.uid}`), {
      email: data.adminEmail.toLowerCase(),
      displayName: data.adminDisplayName,
      role: 'hr_admin',
      roleId: 'sys-hr-admin',
      status: 'active',
      createdAt: serverTimestamp(),
      lastLoginAt: null,
    });

    await this.loadTenants();

    return {
      tenantId,
      adminUserId: adminAuthUser.uid,
      adminTempPassword: adminPassword,
    };
  }

  async updateTenant(tenantId: string, data: UpdateTenantData): Promise<void> {
    const tenantRef = doc(this.firestore, `tenants/${tenantId}`);
    await updateDoc(tenantRef, {
      name: data.name,
      domain: data.domain.toLowerCase(),
      'settings.timezone': data.timezone,
      'settings.locale': data.locale,
      'subscription.plan': data.plan,
      'subscription.seats': data.seats,
      updatedAt: serverTimestamp(),
    });

    await this.loadTenants();
  }

  private async createAdminAuthUser(email: string, password: string) {
    const appName = 'talentpro-platform-provisioning';
    const existing = getApps().find((app) => app.name === appName);
    const app = existing ?? initializeApp(environment.firebase, appName);
    const auth = getAuth(app);

    const result = await createUserWithEmailAndPassword(auth, email, password);
    await signOut(auth);
    return result.user;
  }

  private generateTempPassword(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#?';
    const length = 12;
    let password = '';
    for (let i = 0; i < length; i += 1) {
      password += chars[Math.floor(Math.random() * chars.length)];
    }
    return password;
  }
}
